/**
 * SaveConflictModal.tsx
 * A03 : modal de résolution de conflit multi-onglets / multi-sessions.
 *
 * S'ouvre quand UnifiedSaveService détecte qu'une autre session a modifié le
 * projet entre-temps (événement window 'snapbooth:save-conflict'). L'écrasement
 * cloud est BLOQUÉ tant que l'utilisateur n'a pas choisi :
 *  - Recharger la version distante  → recharge la page (récupère la version base)
 *  - Écraser quand même             → force la sauvegarde de la version locale
 *  - Annuler                        → continue en local, synchro cloud suspendue
 *
 * Le travail local n'est jamais perdu (sauvegarde localStorage déjà effectuée).
 */
import React, { useEffect, useState } from 'react';
import { Modal } from '../../../ui/Modal';

export function SaveConflictModal(): React.ReactElement | null {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onConflict = () => setOpen(true);
    window.addEventListener('snapbooth:save-conflict', onConflict);
    return () => window.removeEventListener('snapbooth:save-conflict', onConflict);
  }, []);

  if (!open) return null;

  const handleReload = () => {
    // Recharge la page → l'éditeur re-récupère la version distante depuis Supabase.
    window.location.reload();
  };

  const handleOverwrite = () => {
    window.dispatchEvent(new CustomEvent('snapbooth:conflict-overwrite'));
    setOpen(false);
  };

  const handleCancel = () => {
    window.dispatchEvent(new CustomEvent('snapbooth:conflict-cancel'));
    setOpen(false);
  };

  return (
    <Modal
      isOpen={open}
      onClose={handleCancel}
      title="Conflit de sauvegarde"
      size="small"
      showCloseButton={false}
    >
      <div
        style={{ fontSize: 14, color: 'var(--color-text-secondary, #c9c9d4)', lineHeight: 1.55 }}
      >
        Ce projet a été modifié dans un autre onglet ou par une autre session.
        <br />
        Pour éviter d'écraser ces changements, votre sauvegarde dans le cloud a été suspendue.
        <br />
        <strong style={{ color: 'var(--color-text-primary, #fff)' }}>
          Vos modifications restent enregistrées localement.
        </strong>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          marginTop: 24,
        }}
      >
        <button
          type="button"
          onClick={handleReload}
          style={{
            padding: '10px 16px',
            borderRadius: 8,
            border: 'none',
            background: 'var(--color-primary, #7C3AED)',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          Recharger la version distante
          <span style={{ display: 'block', fontSize: 11, fontWeight: 400, opacity: 0.85 }}>
            Récupère la version à jour (vos changements locaux non sauvegardés seront perdus).
          </span>
        </button>
        <button
          type="button"
          onClick={handleOverwrite}
          style={{
            padding: '10px 16px',
            borderRadius: 8,
            border: '1px solid var(--color-danger, #ef4444)',
            background: 'transparent',
            color: 'var(--color-danger, #ef4444)',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          Écraser quand même
          <span style={{ display: 'block', fontSize: 11, fontWeight: 400, opacity: 0.85 }}>
            Remplace la version distante par la vôtre.
          </span>
        </button>
        <button
          type="button"
          onClick={handleCancel}
          style={{
            padding: '10px 16px',
            borderRadius: 8,
            border: '1px solid var(--color-border, rgba(255,255,255,0.15))',
            background: 'transparent',
            color: 'var(--color-text-primary, #fff)',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          Annuler
          <span style={{ display: 'block', fontSize: 11, fontWeight: 400, opacity: 0.7 }}>
            Continuer en local (synchro cloud suspendue jusqu'à décision).
          </span>
        </button>
      </div>
    </Modal>
  );
}
